import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import listItems from 'flarum/common/helpers/listItems';
import classList from 'flarum/common/utils/classList';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import IndexPage from 'flarum/forum/components/IndexPage';
import { tagTree } from '../tags';
import { markTagSeen, tagUnread } from '../visits';
import { closeDrawer } from '../drawer';
import { canStart, currentTag, extensionEnabled, startDiscussion, t, ts } from '../util';

/**
 * Core's sidebar navigation, minus what Concourse draws itself.
 *
 * 🚨 Built from `IndexSidebar.navItems()` rather than drawn from scratch, so
 * everything other extensions add there — private discussions, a page link,
 * a leaderboard — still appears. Dropped: "All discussions" (our Home),
 * Following (ours, with the others at the top), and flarum/tags' own tag
 * links (the tree below).
 */
const OURS = /^(allDiscussions|following|tags|separator|moreTags|tag\d+)$/;
function extraNavItems() {
  let items;
  try {
    items = IndexSidebar.prototype.navItems.call(Object.create(IndexSidebar.prototype));
  } catch (e) {
    return [];
  }
  for (const key of Object.keys(items.toObject())) {
    if (OURS.test(key)) items.remove(key);
  }
  return items.toArray();
}
const COLLAPSED = 'concourse.tagsCollapsed';
const MORE_COLLAPSED = 'concourse.moreCollapsed';
function readCollapsed(key) {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * The left sidebar: Home, Following, Admin, New Discussion, whatever other
 * extensions add, the tag tree, and the admin's footer links.
 *
 * Mounted once, outside the router (see shell.tsx), so it is the same element
 * on every page — the discussion page and profiles included.
 */
export default class Sidebar extends Component {
  collapsed = readCollapsed(COLLAPSED);
  moreCollapsed = readCollapsed(MORE_COLLAPSED);
  view() {
    const user = app.session.user;
    const route = m.route.get() || '/';
    const onIndex = app.current.matches(IndexPage);
    const active = onIndex ? currentTag() : null;
    const following = route.startsWith('/following');
    const home = onIndex && !active && !following && !app.search.state.params().q;
    const tree = tagTree();
    const footer = (app.forum.attribute('concourseFooterLinks') || []).filter(l => l && l.url);
    return m("nav", {
      className: "ConcourseSidebar",
      "aria-label": ts('sidebar.label')
    }, m("ul", {
      className: "ConcourseNav"
    }, m("li", null, m(Link, {
      href: app.route('index'),
      className: classList('ConcourseNav-item', {
        active: home
      }),
      "aria-current": home ? 'page' : undefined,
      onclick: closeDrawer
    }, m(Icon, {
      name: "fas fa-house",
      className: "ConcourseNav-icon"
    }), m("span", {
      className: "ConcourseNav-label"
    }, t('sidebar.home')))), user && extensionEnabled('flarum-subscriptions') ? m("li", null, m(Link, {
      href: app.route('following'),
      className: classList('ConcourseNav-item', {
        active: following
      }),
      "aria-current": following ? 'page' : undefined,
      onclick: closeDrawer
    }, m(Icon, {
      name: "far fa-file-lines",
      className: "ConcourseNav-icon"
    }), m("span", {
      className: "ConcourseNav-label"
    }, t('sidebar.following')))) : null, user && user.isAdmin() ? m("li", null, m("a", {
      href: app.forum.attribute('adminUrl'),
      className: "ConcourseNav-item"
    }, m(Icon, {
      name: "fas fa-wrench",
      className: "ConcourseNav-icon"
    }), m("span", {
      className: "ConcourseNav-label"
    }, t('sidebar.admin')))) : null, canStart() ? m("li", null, m("button", {
      type: "button",
      className: "ConcourseNav-item",
      onclick: () => {
        closeDrawer();
        startDiscussion();
      }
    }, m(Icon, {
      name: "far fa-square-plus",
      className: "ConcourseNav-icon"
    }), m("span", {
      className: "ConcourseNav-label"
    }, app.translator.trans('core.forum.index.start_discussion_button')))) : null), tree.primary.length || tree.secondary.length ? m("section", {
      className: classList('ConcourseTags', {
        collapsed: this.collapsed
      })
    }, m("h2", {
      className: "ConcourseTags-heading"
    }, m("button", {
      type: "button",
      className: "ConcourseTags-toggle",
      "aria-expanded": this.collapsed ? 'false' : 'true',
      onclick: () => this.toggle()
    }, m(Icon, {
      name: "fas fa-chevron-down",
      className: "ConcourseTags-chevron"
    }), m("span", {
      className: "ConcourseNav-label"
    }, t('sidebar.tags')))), this.collapsed ? null : m("ul", {
      className: "ConcourseTags-list"
    }, tree.primary.map(({
      tag,
      children
    }) => [this.tagLink(tag, active, false), children.length ? m("li", {
      className: "ConcourseTags-children"
    }, m("ul", null, children.map(child => this.tagLink(child, active, true)))) : null]), tree.secondary.map(tag => this.tagLink(tag, active, false)), m("li", null, m(Link, {
      href: app.route('tags'),
      className: "ConcourseTag ConcourseTag--all",
      onclick: closeDrawer
    }, m(Icon, {
      name: "fas fa-list",
      className: "ConcourseTag-icon"
    }), m("span", {
      className: "ConcourseTag-name"
    }, t('sidebar.all_tags')))))) : null, this.extras(), footer.length ? m("footer", {
      className: "ConcourseSidebar-footer"
    }, footer.map((link, i) => [i ? m("span", {
      "aria-hidden": "true"
    }, " \xB7 ") : null, /^\//.test(link.url) ? m(Link, {
      href: link.url
    }, link.label) : m("a", {
      href: link.url,
      rel: "noopener"
    }, link.label)])) : null);
  }

  /**
   * Whatever other extensions add to the sidebar, in a section of its own
   * AFTER the tag tree: a forum with fifteen extensions must not push its
   * tags off the screen.
   */
  extras() {
    const nav = extraNavItems();
    if (!nav.length) return null;
    return m("section", {
      className: classList('ConcourseTags ConcourseMore', {
        collapsed: this.moreCollapsed
      })
    }, m("h2", {
      className: "ConcourseTags-heading"
    }, m("button", {
      type: "button",
      className: "ConcourseTags-toggle",
      "aria-expanded": this.moreCollapsed ? 'false' : 'true',
      onclick: () => this.toggle('more')
    }, m(Icon, {
      name: "fas fa-chevron-down",
      className: "ConcourseTags-chevron"
    }), m("span", {
      className: "ConcourseNav-label"
    }, t('sidebar.more')))), this.moreCollapsed ? null : m("ul", {
      className: "ConcourseNav ConcourseNav--extra",
      onclick: closeDrawer
    }, listItems(nav)));
  }
  toggle(which = 'tags') {
    const key = which === 'tags' ? COLLAPSED : MORE_COLLAPSED;
    const next = which === 'tags' ? this.collapsed = !this.collapsed : this.moreCollapsed = !this.moreCollapsed;
    try {
      window.localStorage.setItem(key, next ? '1' : '0');
    } catch (e) {
      // Not remembered; it still toggles for this visit.
    }
  }
  tagLink(tag, active, child) {
    const isActive = active === tag;
    const unread = !isActive && tagUnread(tag);

    // Unkeyed on purpose: the list mixes fragments (a tag with its children)
    // and single rows, and Mithril refuses keyed and unkeyed siblings.
    return m("li", null, m(Link, {
      href: app.route('tag', {
        tags: tag.slug()
      }),
      className: classList('ConcourseTag', {
        active: isActive,
        'ConcourseTag--child': child
      }),
      "aria-current": isActive ? 'page' : undefined,
      onclick: () => {
        markTagSeen(tag.id());
        closeDrawer();
      }
    }, child ? null : m("span", {
      className: "ConcourseTag-swatch",
      style: {
        '--tag-color': tag.color() || 'var(--cc-faint)'
      },
      "aria-hidden": "true"
    }), m("span", {
      className: "ConcourseTag-name"
    }, tag.name()), unread ? m("span", {
      className: "ConcourseTag-dot",
      role: "img",
      "aria-label": ts('sidebar.unread')
    }) : null));
  }
}
flarum.reg.add('ernestdefoe-concourse', 'forum/components/Sidebar', Sidebar);